import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAssignmentRequestStore } from '../../store/assignmentRequestStore';
import { RequestCard } from './RequestCard';
import { SegmentPills, type SegmentPill } from '../shared/SegmentPills';
import type { AssignmentRequestFilter } from '../../types';

interface Props { variant: 'inbox' | 'widget' | 'strip'; view?: 'queue' | 'mine' }

// Counts aren't wired per status in v1 → each pill shows SegmentPills' '—' placeholder.
const FILTER_PILLS: SegmentPill[] = [
  { key: 'PENDING', label: 'Pending', count: null, tint: 'amber' },
  { key: 'HELD', label: 'Held', count: null, tint: 'blue' },
  { key: 'DECIDED', label: 'Decided', count: null, tint: 'gray' },
];

export function PendingRequests({ variant, view = 'queue' }: Props) {
  const { requests, isLoading, fetchQueue, fetchMine, fetchCount } = useAssignmentRequestStore();
  const [filter, setFilter] = useState<AssignmentRequestFilter>('PENDING');
  const effectiveFilter: AssignmentRequestFilter = variant === 'inbox' ? filter : 'PENDING';

  useEffect(() => {
    if (view === 'mine') fetchMine(effectiveFilter); else fetchQueue(effectiveFilter);
    fetchCount();
  }, [view, effectiveFilter, fetchQueue, fetchMine, fetchCount]);

  // INBOX: the status filter is always visible (so an empty tab isn't a dead-end); list/empty render below it.
  if (variant === 'inbox') {
    return (
      <div className="space-y-3">
        <SegmentPills pills={FILTER_PILLS} activeKey={filter} onSelect={(k) => setFilter(k as AssignmentRequestFilter)} />
        {isLoading && requests.length === 0
          ? <div className="text-sm text-gray-400 dark:text-gray-500">Loading…</div>
          : requests.length === 0
            ? <div className="card p-6 text-sm text-gray-500 dark:text-gray-400">No requests.</div>
            : <div className="space-y-3">{requests.map((r) => <RequestCard key={r.id} request={r} layout="full" />)}</div>}
      </div>
    );
  }

  // WIDGET / STRIP: no filter, always Pending; strip stays invisible when empty.
  if (isLoading && requests.length === 0) {
    return <div className="text-sm text-gray-400 dark:text-gray-500">Loading…</div>;
  }
  if (requests.length === 0) {
    if (variant === 'strip') return null;
    return <div className="card p-6 text-sm text-gray-500 dark:text-gray-400">No pending requests.</div>;
  }
  const shown = requests.slice(0, variant === 'widget' ? 3 : 2);
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Pending requests ({requests.length})</h2>
        <Link to="/requests" className="text-xs font-medium text-blast-700 dark:text-blast-300 hover:underline">View all</Link>
      </div>
      {shown.map((r) => <RequestCard key={r.id} request={r} layout="compact" />)}
    </div>
  );
}
